import React, { useState, useRef, useEffect } from 'react';
import { useTheme } from '../context/ThemeContext';
import { THEMES_CONFIG } from '../config/themes';
import { getHolidayTheme } from '../config/holidaySchedule';

const EFFECTS_KEY   = 'bv_effects_off';
const THEME_OFF_KEY = 'bv_holiday_off';

// Safe localStorage — кидає SecurityError в Safari Private Mode
function lsGet(key) { try { return localStorage.getItem(key); } catch { return null; } }
function lsSet(key, val) { try { localStorage.setItem(key, val); } catch {} }
function lsRemove(key) { try { localStorage.removeItem(key); } catch {} }

const ThemeSelector = ({ showFullPicker }) => {
  const { theme, setTheme, setAutoTheme } = useTheme();
  const [open, setOpen]               = useState(false);
  const [effectsOn, setEffectsOn]     = useState(() => lsGet(EFFECTS_KEY) !== 'true');
  const [holidayOn, setHolidayOn]     = useState(() => lsGet(THEME_OFF_KEY) !== 'true');
  const ref = useRef(null);

  const holidayTheme  = getHolidayTheme();
  const holidayConfig = THEMES_CONFIG[holidayTheme];

  // Закрити при кліку зовні
  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);

  // Авто-тема при старті свята (тільки при зміні holidayTheme, не при монтуванні)
  const prevHolidayRef = useRef(holidayTheme);
  useEffect(() => {
    const prev = prevHolidayRef.current;
    prevHolidayRef.current = holidayTheme;
    // Якщо нічого не змінилось — не робимо нічого
    if (prev === holidayTheme) return;
    if (holidayTheme) {
      const wasOff = lsGet(THEME_OFF_KEY) === 'true';
      if (!wasOff) {
        setAutoTheme();
        setHolidayOn(true);
        setEffectsOn(true);
        lsRemove(EFFECTS_KEY);
      }
    } else {
      // Свято закінчилось — скидаємо і повертаємо дефолт
      lsRemove(THEME_OFF_KEY);
      setAutoTheme();
    }
  }, [holidayTheme]); // eslint-disable-line

  const toggleEffects = (e) => {
    e.stopPropagation();
    const next = !effectsOn;
    setEffectsOn(next);
    lsSet(EFFECTS_KEY, next ? 'false' : 'true');
    window.dispatchEvent(new Event('bv_effects_changed'));
  };

  const toggleHoliday = (e) => {
    e.stopPropagation();
    const next = !holidayOn;
    setHolidayOn(next);
    if (next) {
      // Вмикаємо свято — спочатку прибираємо флаг, потім авто-тема підхопить свято
      lsRemove(THEME_OFF_KEY);
      setAutoTheme();
      setEffectsOn(true);
      lsRemove(EFFECTS_KEY);
      window.dispatchEvent(new Event('bv_effects_changed'));
    } else {
      // Вимикаємо свято — спочатку ставимо флаг, тоді setAutoTheme поверне base (не свято)
      lsSet(THEME_OFF_KEY, 'true');
      // Примусово скидаємо manual flag щоб setAutoTheme спрацював
      lsRemove('bv_theme_manual');
      setAutoTheme();
      window.dispatchEvent(new Event('bv_effects_changed'));
    }
  };

  const isVisible = !!holidayTheme || showFullPicker;
  const grad = holidayConfig?.cssVars?.['--bv-gradient-brand'] || holidayConfig?.swatch || '#e8390a';

  if (!isVisible) return null;

  return (
    <div className="theme-selector" ref={ref}>
      <button
        className={`theme-toggle action-btn ${open ? 'active' : ''}`}
        onClick={() => setOpen(o => !o)}
        title="Theme"
        type="button"
      >
        <svg viewBox="0 0 24 24" fill="none" width="20" height="20">
          <circle cx="12" cy="12" r="4" fill="currentColor" opacity="0.3"/>
          <circle cx="12" cy="12" r="4" stroke="currentColor" strokeWidth="1.8"/>
          <path d="M12 2v2M12 20v2M4.22 4.22l1.42 1.42M18.36 18.36l1.42 1.42M2 12h2M20 12h2M4.22 19.78l1.42-1.42M18.36 5.64l1.42-1.42"
            stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/>
        </svg>
      </button>

      {open && (
        <div className="theme-dropdown">
          <p className="theme-dropdown__title">Theme</p>

          {showFullPicker ? (
            /* ── Повний список ── */
            <>
              <div className="theme-dropdown__list">
                {Object.values(THEMES_CONFIG).map(t => {
                  const isActive = theme === t.id;
                  const tGrad = t.cssVars?.['--bv-gradient-brand'] || t.swatch;
                  return (
                    <button key={t.id}
                      className={`theme-option ${isActive ? 'theme-option--active' : ''}`}
                      onClick={() => { setTheme(t.id); setOpen(false); }}
                      type="button"
                    >
                      <span className="theme-option__swatch" style={{ background: tGrad }}>
                        {isActive && <svg viewBox="0 0 10 8" fill="none" width="10" height="8"><path d="M1 4l2.5 2.5L9 1" stroke="#fff" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/></svg>}
                      </span>
                      <span className="theme-option__emoji">{t.emoji}</span>
                      <span className="theme-option__label">{t.label}</span>
                    </button>
                  );
                })}
              </div>
              {/* Effects toggle — завжди видимий у повному списку */}
              <div className="theme-dropdown__divider"/>
              <button className="theme-option theme-option--effects" onClick={toggleEffects} type="button">
                <span className="theme-option__swatch theme-option__swatch--effects">{effectsOn ? '✨' : '🚫'}</span>
                <span className="theme-option__label">Effects {effectsOn ? 'On' : 'Off'}</span>
                <span className={`theme-effects-toggle ${effectsOn ? 'theme-effects-toggle--on' : ''}`}>
                  <span className="theme-effects-toggle__knob"/>
                </span>
              </button>
            </>
          ) : (
            /* ── Спрощений (тільки свято) ── */
            <>
              <button className={`theme-option ${!holidayOn ? 'theme-option--active' : ''}`}
                onClick={() => { lsSet(THEME_OFF_KEY,'true'); lsRemove('bv_theme_manual'); setHolidayOn(false); setAutoTheme(); setOpen(false); window.dispatchEvent(new Event('bv_effects_changed')); }}
                type="button">
                <span className="theme-option__swatch" style={{ background: THEMES_CONFIG[window._bvS?.forceTheme]?.cssVars?.['--bv-gradient-brand'] || THEMES_CONFIG.orange?.cssVars?.['--bv-gradient-brand'] || '#e8390a' }}>
                  {!holidayOn && <svg viewBox="0 0 10 8" fill="none" width="10" height="8"><path d="M1 4l2.5 2.5L9 1" stroke="#fff" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/></svg>}
                </span>
                <span className="theme-option__emoji">{THEMES_CONFIG[window._bvS?.forceTheme]?.emoji || '🍊'}</span>
                <span className="theme-option__label">{THEMES_CONFIG[window._bvS?.forceTheme]?.label || 'Original'}</span>
              </button>

              <button className={`theme-option ${holidayOn ? 'theme-option--active' : ''}`}
                onClick={toggleHoliday} type="button">
                <span className="theme-option__swatch" style={{ background: grad }}>
                  {holidayOn && <svg viewBox="0 0 10 8" fill="none" width="10" height="8"><path d="M1 4l2.5 2.5L9 1" stroke="#fff" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/></svg>}
                </span>
                <span className="theme-option__emoji">{holidayConfig?.emoji}</span>
                <span className="theme-option__label">{holidayConfig?.label}</span>
                <span className={`theme-effects-toggle ${holidayOn ? 'theme-effects-toggle--on' : ''}`}>
                  <span className="theme-effects-toggle__knob"/>
                </span>
              </button>

              {holidayOn && (
                <button className="theme-option theme-option--effects" onClick={toggleEffects} type="button">
                  <span className="theme-option__swatch theme-option__swatch--effects">{effectsOn ? '✨' : '🚫'}</span>
                  <span className="theme-option__label">Effects {effectsOn ? 'On' : 'Off'}</span>
                  <span className={`theme-effects-toggle ${effectsOn ? 'theme-effects-toggle--on' : ''}`}>
                    <span className="theme-effects-toggle__knob"/>
                  </span>
                </button>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default ThemeSelector;


// Обгортка що завжди залишається змонтованою
const ThemeSelectorWrapper = () => {
  const [showPicker, setShowPicker] = React.useState(
    () => window._bvS?.showThemePicker === 'TRUE' || window._bvS?.showThemePicker === true
  );
  const prevShowPicker = React.useRef(null); // null = settings ще не завантажились
  const { setAutoTheme } = useTheme();

  React.useEffect(() => {
    const h = () => {
      const nowShowPicker = window._bvS?.showThemePicker === 'TRUE' ||
                            window._bvS?.showThemePicker === true;

      if (prevShowPicker.current === null) {
        // Перший раз — просто запам'ятовуємо, не скидаємо тему
        prevShowPicker.current = nowShowPicker;
      } else if (prevShowPicker.current !== nowShowPicker) {
        // Реальна зміна адміном — скидаємо тему
        prevShowPicker.current = nowShowPicker;
        setAutoTheme();
      }

      setShowPicker(nowShowPicker);
    };
    window.addEventListener('bv_settings_updated', h);
    return () => window.removeEventListener('bv_settings_updated', h);
  }, [setAutoTheme]);

  const holidayNow = typeof getHolidayTheme === 'function' ? getHolidayTheme() : null;

  if (!showPicker && !holidayNow) return null;

  return <ThemeSelector showFullPicker={showPicker} />;
};

export { ThemeSelectorWrapper };
